import { WorkflowIcon } from '@alison/ui';
import ArticleOutlined from '@mui/icons-material/ArticleOutlined';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { useNavigate } from '@tanstack/react-router';
import { type ReactNode, useEffect, useRef } from 'react';
import { formatRelativeTime } from '../../lib/notificationFeed';
import { ACTIVITY_POLL_MS, useOutcomes, useSessionList } from '../../lib/workflows.hooks';
import { MediaCard } from '../media/shared/MediaCard';
import { KIND_ICON } from '../outcomes/OutcomeIcons';
import { runLabelOf } from '../workflows/automations/RunLabel';
import { humanize } from '../workflows/catalogFilters';
import { IconBadge } from '../workflows/IconBadge';
import { Pill, type PRIMARY_PILL_SX, STATUS_PILL_SX } from '../workflows/Pill';
import { failureReason } from '../workflows/runFailure';
import { iconFor } from '../workflows/workflowIcons';
import {
  ACTIVITY_LIMIT,
  type ActivityRow,
  type ActivityStatus,
  activityRows,
  STATUS_PILL,
} from './activityRows';
import { SectionHeader } from './SectionHeader';

const ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 3,
  width: '100%',
  px: 4,
  py: 3,
  font: 'inherit',
  textAlign: 'left',
  color: 'inherit',
  '& + &': { borderTop: '1px solid', borderColor: 'divider' },
  '&:hover': { bgcolor: 'action.hover' },
};

interface ActivityLineProps {
  title: string;
  pill: string;
  pillSx?: typeof PRIMARY_PILL_SX;
  detail: string;
  icon: ReactNode;
  onClick: () => void;
}

interface ActivitySectionProps {
  accountIds: number[];
}

function ActivityLine({
  title,
  pill,
  pillSx,
  detail,
  icon,
  onClick,
}: ActivityLineProps): ReactNode {
  return (
    <ButtonBase onClick={onClick} sx={ROW_SX}>
      {icon}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography noWrap sx={{ fontSize: 14, fontWeight: 600 }}>
            {title}
          </Typography>
          <Pill {...(pillSx ? { sx: pillSx } : {})}>{pill}</Pill>
        </Box>
        <Typography noWrap sx={{ color: 'text.muted' }}>
          {detail}
        </Typography>
      </Box>
      <ChevronRight sx={{ color: 'text.muted' }} />
    </ButtonBase>
  );
}

export function ActivitySection({ accountIds }: ActivitySectionProps): ReactNode {
  const navigate = useNavigate();
  const sessionList = useSessionList(ACTIVITY_LIMIT, accountIds);
  const hasRunning = sessionList.data?.sessions.some((session) => session.status === 'running');
  const outcomes = useOutcomes(ACTIVITY_LIMIT, hasRunning ? ACTIVITY_POLL_MS : false, accountIds);
  const rows = activityRows(sessionList.data?.sessions ?? [], outcomes.outcomes);
  const isPending = sessionList.isPending || outcomes.isPending;
  const wasRunning = useRef(false);
  const refetchOutcomes = outcomes.retry;
  useEffect(() => {
    if (wasRunning.current && !hasRunning) refetchOutcomes();
    wasRunning.current = hasRunning === true;
  }, [hasRunning, refetchOutcomes]);

  const renderRow = (row: ActivityRow): ReactNode => {
    if (row.kind === 'completed') {
      const { outcome } = row;
      const Glyph = iconFor(outcome.icon ?? '', KIND_ICON[outcome.kind] ?? ArticleOutlined);
      const when = formatRelativeTime(new Date(outcome.completedAt));
      return (
        <ActivityLine
          key={row.key}
          icon={<IconBadge icon={Glyph} size={32} />}
          title={outcome.title || outcome.workflow}
          pill={humanize(outcome.kind).toUpperCase()}
          detail={outcome.creator === null ? when : `${when} · ${outcome.creator}`}
          onClick={() =>
            void navigate({
              to: '/outcomes',
              search: (previous) => ({ ...previous, openRunId: outcome.runId }),
            })
          }
        />
      );
    }
    const { session, status } = row;
    const currentActionType = session.actionTypes.at(-1) ?? '';
    const updated = `Updated ${formatRelativeTime(new Date(session.updatedAt))}`;
    const reason = status === 'failed' ? failureReason(session.error) : null;
    return (
      <ActivityLine
        key={row.key}
        icon={<IconBadge icon={iconFor(session.icon, WorkflowIcon)} size={32} />}
        title={session.workflow}
        pill={STATUS_PILL[status]}
        {...(STATUS_PILL_SX[status] ? { pillSx: STATUS_PILL_SX[status] } : {})}
        detail={[
          session.automation === null ? null : runLabelOf(session.automation),
          reason,
          updated,
        ]
          .filter((part) => part !== null)
          .join(' · ')}
        onClick={() =>
          void navigate({
            to: '/workflows/$workflowId',
            params: { workflowId: currentActionType },
            search: (previous) => ({ ...previous, run: session.latestRunId }),
          })
        }
      />
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <SectionHeader
        title="Activity"
        subtitle="Runs and the outcomes they produced — newest first"
        linkLabel="Full activity log →"
        onLinkClick={() => void navigate({ to: '/outcomes', search: (previous) => previous })}
      />
      {(sessionList.isError || outcomes.isError) && rows.length === 0 ? (
        <Alert severity="error">Activity could not be loaded.</Alert>
      ) : isPending ? (
        <MediaCard sx={{ p: 4 }}>
          <Skeleton variant="rounded" height={120} />
        </MediaCard>
      ) : rows.length === 0 ? (
        <Typography sx={{ color: 'text.secondary' }}>
          No runs yet. Start a workflow to see it here.
        </Typography>
      ) : (
        <MediaCard sx={{ overflow: 'hidden' }}>{rows.map(renderRow)}</MediaCard>
      )}
    </Box>
  );
}
